/**
 * Click-to-WhatsApp source code — invisible-character encoding.
 *
 * A 48-bit code (12 hex chars, minted by the source-resolver API) is encoded
 * base-4 over four invisible Unicode code points and embedded inside the
 * pre-filled WhatsApp message. The receiving side decodes it from the first
 * inbound message to recover the click's attribution.
 *
 * Alphabet notes — chosen so the run survives real-world text handling:
 *  - U+200C (ZWNJ) is excluded: it is semantically meaningful in Indic scripts.
 *  - U+200D (ZWJ) is excluded: it alters emoji rendering and gets normalised.
 *  - U+FEFF is excluded: JavaScript String.trim() removes it.
 * The run is framed by fixed head/tail sentinels and carries an 8-bit FNV-1a
 * checksum, so stray zero-width characters from CMS copy can never be misread
 * as a code, and a partially stripped run fails closed.
 */

const ALPHABET = ["​", "⁠", "⁡", "⁢"];
const SYMBOL_INDEX = new Map(ALPHABET.map((ch, i) => [ch, i]));

const HEAD = [0, 3, 0, 3];
const TAIL = [3, 0, 3, 0];
const ID_SYMBOLS = 24; // 48 bits, 2 bits per symbol
const CHECKSUM_SYMBOLS = 4; // 8 bits
export const RUN_LENGTH =
  HEAD.length + ID_SYMBOLS + CHECKSUM_SYMBOLS + TAIL.length; // 36

const CODE_RE = /^[0-9a-f]{12}$/i;

// Every invisible/format character we may ever emit or receive, for stripping.
const INVISIBLES_RE = /[​-‏⁠-⁤﻿]/g;

function checksum8(id: bigint): number {
  let h = 0x811c9dc5;
  for (let i = 5; i >= 0; i--) {
    h ^= Number((id >> BigInt(8 * i)) & 0xffn);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h & 0xff;
}

function toSymbols(value: bigint, count: number): number[] {
  return Array.from({ length: count }, (_, i) =>
    Number((value >> BigInt(2 * (count - 1 - i))) & 3n),
  );
}

export function stripInvisibles(text: string): string {
  return text ? text.replace(INVISIBLES_RE, "") : "";
}

export function encodeToken(codeHex: string): string {
  if (!CODE_RE.test(codeHex)) return "";
  const id = BigInt("0x" + codeHex);
  return [
    ...HEAD,
    ...toSymbols(id, ID_SYMBOLS),
    ...toSymbols(BigInt(checksum8(id)), CHECKSUM_SYMBOLS),
    ...TAIL,
  ]
    .map((s) => ALPHABET[s])
    .join("");
}

export function decodeToken(text: string): string | null {
  if (!text) return null;

  const runs: number[][] = [];
  let current: number[] = [];
  for (const ch of text) {
    const symbol = SYMBOL_INDEX.get(ch);
    if (symbol === undefined) {
      if (current.length) runs.push(current);
      current = [];
    } else {
      current.push(symbol);
    }
  }
  if (current.length) runs.push(current);

  for (const run of runs) {
    if (run.length !== RUN_LENGTH) continue;
    if (!HEAD.every((v, i) => run[i] === v)) continue;
    if (!TAIL.every((v, i) => run[RUN_LENGTH - TAIL.length + i] === v))
      continue;

    let id = 0n;
    for (let i = 0; i < ID_SYMBOLS; i++) {
      id = (id << 2n) | BigInt(run[HEAD.length + i]);
    }
    let checksum = 0;
    for (let i = 0; i < CHECKSUM_SYMBOLS; i++) {
      checksum = (checksum << 2) | run[HEAD.length + ID_SYMBOLS + i];
    }
    if (checksum !== checksum8(id)) continue;

    return id.toString(16).padStart(12, "0");
  }
  return null;
}

/**
 * Embeds the code after the first word — never at the edges, where trimming
 * and normalisation are most likely to strike. Pre-existing invisible
 * characters (CMS copy-paste artefacts) are stripped first so the message
 * carries exactly one decodable run.
 */
export function injectToken(message: string, codeHex: string): string {
  const clean = stripInvisibles(message);
  const run = encodeToken(codeHex);
  if (!run) return clean;
  const firstSpace = clean.indexOf(" ");
  return firstSpace === -1
    ? clean + run
    : clean.slice(0, firstSpace) + run + clean.slice(firstSpace);
}
